import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import Seo from "../components/Seo";
import {
  BookOpen, ArrowRight, Heart, Headphones, GraduationCap,
  CheckCircle2, Quote, Star, ShieldCheck, Award, Download,
} from "lucide-react";

/* /with-faith — SALES PAGE for "With Faith All Things Are Possible"
   Book · Video Course · Audio Program — the bundle.
   Visual language mirrors the bundle hero artwork: navy + gold + sunrise.
   Chapter cards pair the actual chapter images with the real opening
   excerpt from Mike's manuscript so readers can taste the prose and
   want the rest. */

const NAVY = "#0F172A";
const NAVY_DK  = "#0F1A2E";
const GOLD     = "#E8B539";
const GOLD_DK  = "#2E9F3A";
const PAPER = "#FFFFFF";
const INK      = "#0F1A2E";
const WINE     = "#0F172A";

/* ────────────────────────────────────────────────────────────────
   The 10 chapters — title + opening excerpt taken verbatim from the
   June 2, 2026 final manuscript. Image goes with the matching number:
   /with-faith/chapters/chapter-XX.png
   ──────────────────────────────────────────────────────────────── */
const CHAPTERS = [
  {
    n: 1,
    title: "Why Most People Feel Stuck",
    excerpt:
      "Most people want a better life. They want stronger faith, better health, more money, less stress, and a sense of purpose. They dream about having more freedom and more time to enjoy life, but year after year they find themselves facing the same challenges.",
  },
  {
    n: 2,
    title: "God Created You for More",
    excerpt:
      "Most people never reach their full potential because they spend too much time focusing on their limitations. It is easy to look at your bank account, your job, your age, your education, or your past mistakes and decide that your future is already written. Nothing could be further from the truth.",
  },
  {
    n: 3,
    title: "Building a Strong Foundation of Faith",
    excerpt:
      "Faith is not something you use only when life falls apart. Faith is something you build before problems arrive. Just like a house needs a solid foundation before the walls go up, a successful life needs a strong spiritual foundation before growth can happen in other areas.",
  },
  {
    n: 4,
    title: "The Connection Between Faith and Wellness",
    excerpt:
      "God cares about every part of your life, including your health. The body you live in affects your energy, your focus, your attitude, and your ability to serve others. Taking care of yourself is not selfish. It is stewardship.",
  },
  {
    n: 5,
    title: "Discovering Your Gifts and Talents",
    excerpt:
      "Most people spend their lives looking for something they already have. They search for purpose. They search for direction. They search for the one thing that will finally make life make sense. What many never realize is that God has already placed gifts and talents inside them.",
  },
  {
    n: 6,
    title: "When God's Plan Changes Yours",
    excerpt:
      "Most people have a picture in their mind of how life is supposed to go. They expect certain doors to open, certain opportunities to appear, and certain dreams to happen on a specific timetable. Then life happens.",
  },
  {
    n: 7,
    title: "Turning Your Gifts into Service",
    excerpt:
      "God did not give you gifts and talents so they could sit on a shelf unused. He gave them to you for a reason. Every ability, skill, life lesson, and experience you have can be used to help someone else. The question is not whether you have something valuable to offer. The question is whether you are willing to use it.",
  },
  {
    n: 8,
    title: "Using Modern Tools for God's Glory",
    excerpt:
      "Every generation is given tools. The question is not whether the tools are good or bad. The question is how they are used. A hammer can build a house or break a window. A printing press can spread truth or spread lies. The internet, social media, artificial intelligence, online education, and digital business are no different.",
  },
  {
    n: 9,
    title: "Building Something That Lasts",
    excerpt:
      "We live in a world that often focuses on quick results. People want success overnight. They want instant wealth, instant recognition, and instant solutions to problems that may have taken years to develop. The truth is that most things worth having take time to build.",
  },
  {
    n: 10,
    title: "Your Next Chapter Starts Today",
    excerpt:
      "If you have made it this far, you have already done something many people never do. You have taken time to think about your life, your faith, your purpose, and the direction you want to go. That may not sound like a major accomplishment, but it is often the first step toward meaningful change.",
  },
];

/* The bundle hero image (book + laptop course + headphones + phone audio).
   File lives at /app/frontend/public/with-faith/bundle-hero.png — uploaded
   by Mike June 4, 2026. */
const BUNDLE_IMG = "/with-faith/bundle-hero.png";

/* CTA target — single source of truth so we only update one place. */
const BUY_HREF = "/pricing";

export default function WithFaithBookPage({ embedded = false }) {
  useEffect(() => {
    document.title = "With Faith All Things Are Possible — Book + Course + Audio | Michael Boyd";
  }, []);

  return (
    <div className="min-h-screen" style={{ background: PAPER }} data-testid="with-faith-book-page">
      <Seo
        title="With Faith All Things Are Possible — Book + Video Course + Audio | Michael Boyd"
        description="The complete bundle from Michael Boyd — paperback book, video course, and audio program. Using your God-given talents to build a better life. One message. Three formats."
        canonical="https://faitharama.com/with-faith"
        image="https://faitharama.com/with-faith/bundle-hero.png"
      />
      {!embedded && <SiteNav />}

      {/* ════════════ HERO ════════════ */}
      <section className="relative overflow-hidden border-b-2"
               style={{ background: `linear-gradient(180deg, #FAFCFF 0%, #E7F0FA 60%, #CFE0F2 100%)`,
                        borderColor: GOLD }}>
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
             style={{ background: `linear-gradient(180deg, transparent 0%, ${GOLD}55 100%)` }}/>

        <div className="relative max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 grid lg:grid-cols-[1fr_540px] gap-10 items-center">
          {/* COPY */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase font-bold mb-4 shadow"
                 style={{ background: GOLD, color: NAVY }}>
              <Star className="w-3.5 h-3.5"/> The Faitharama Flagship Bundle
            </div>
            <h1 className="font-serif-display uppercase leading-[0.95]"
                style={{ fontSize: "clamp(2.4rem, 6vw, 5rem)", color: NAVY, textWrap: "balance" }}
                data-testid="with-faith-hero-title">
              With <span style={{ color: GOLD_DK }}>Faith</span>,<br/>
              All Things Are <span style={{ color: GOLD_DK }}>Possible.</span>
            </h1>
            <p className="mt-5 text-xl md:text-2xl font-medium" style={{ color: NAVY }}>
              Using Your God-Given Talents to Build a Better Life.
            </p>
            <p className="mt-2 text-sm md:text-base italic" style={{ color: "#15406B" }}>
              by <strong>Michael Boyd</strong>
            </p>

            {/* WHAT YOU GET — 3-line bundle pitch */}
            <ul className="mt-6 space-y-2.5">
              {[
                { Icon: BookOpen,       label: "Inspiring Book",   sub: "10 chapters of plain-English wisdom" },
                { Icon: GraduationCap,  label: "Video Course",      sub: "Step-by-step lessons that walk you through it" },
                { Icon: Headphones,     label: "Audio Program",     sub: "Listen anywhere, anytime" },
              ].map(({ Icon, label, sub }) => (
                <li key={label} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
                       style={{ background: "#FFFFFF", color: GOLD }}>
                    <Icon className="w-4 h-4"/>
                  </div>
                  <div>
                    <div className="font-bold text-base" style={{ color: NAVY }}>{label}</div>
                    <div className="text-sm" style={{ color: "#3A4A6B" }}>{sub}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to={BUY_HREF}
                    data-testid="with-faith-hero-cta"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base md:text-lg font-bold uppercase tracking-[0.18em] shadow-xl hover:scale-[1.03] transition"
                    style={{ background: GOLD, color: NAVY, border: `3px solid ${NAVY}` }}>
                <Heart className="w-5 h-5"/> Get The Bundle <ArrowRight className="w-5 h-5"/>
              </Link>
              <a href="#chapters"
                 data-testid="with-faith-hero-peek"
                 className="inline-flex items-center gap-2 px-5 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] hover:scale-[1.02] transition"
                 style={{ background: "rgba(22,36,74,0.06)", color: NAVY, border: `2px solid ${NAVY}` }}>
                Peek Inside <ArrowRight className="w-4 h-4"/>
              </a>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: "#3A4A6B" }}>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5"/> 30-Day Guarantee</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5"><Download className="w-3.5 h-3.5"/> Instant Course Access</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5"/> By Michael Boyd</span>
            </div>
          </div>

          {/* HERO IMAGE — the bundle artwork */}
          <div className="relative mx-auto lg:mx-0">
            <div className="absolute inset-0 rounded-2xl pointer-events-none"
                 style={{ background: `radial-gradient(circle, ${GOLD}55 0%, transparent 70%)`,
                          filter: "blur(40px)", transform: "scale(1.05)" }}/>
            <img src={BUNDLE_IMG}
                 alt="With Faith All Things Are Possible — Book, Video Course, and Audio Program bundle"
                 className="relative w-full max-w-[540px] rounded-xl shadow-2xl"
                 style={{ border: `3px solid ${GOLD_DK}` }}
                 data-testid="with-faith-hero-image"/>
          </div>
        </div>
      </section>

      {/* ════════════ STRAP-LINE ════════════ */}
      <section style={{ background: "#FFFFFF" }} className="border-y-2" data-testid="with-faith-strapline">
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-5 text-center">
          <p className="font-serif-display text-lg md:text-2xl tracking-[0.06em]" style={{ color: "#E0F2FE" }}>
            One Message <span style={{ color: GOLD }}>·</span> Three Formats <span style={{ color: GOLD }}>·</span> Endless Possibilities
          </p>
        </div>
      </section>

      {/* ════════════ HOOK ════════════ */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center">
        <Quote className="w-9 h-9 mx-auto mb-3" style={{ color: GOLD_DK }}/>
        <p className="font-serif-display text-2xl md:text-3xl italic leading-snug" style={{ color: NAVY, textWrap: "balance" }}>
          "For with God nothing shall be impossible."
        </p>
        <p className="text-sm uppercase tracking-[0.28em] font-bold mt-2" style={{ color: GOLD_DK }}>— Luke 1:37</p>
        <p className="mt-6 text-lg md:text-xl leading-relaxed" style={{ color: INK }}>
          This isn't another motivational book. It's a movement — a faith-and-purpose guide for the everyday Christian who's ready to stop hustling on the world's system and start building on God's.
        </p>
      </section>

      {/* ════════════ CHAPTER PREVIEW WALL ════════════ */}
      <ChapterWall />

      {/* ════════════ WHAT'S INSIDE THE BUNDLE ════════════ */}
      <section className="border-y-2" style={{ background: "#FFFFFF", borderColor: GOLD }} data-testid="with-faith-bundle">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16 text-center text-white">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-3" style={{ color: GOLD }}>
            ★ YOUR COMPLETE BUNDLE
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl uppercase leading-[1.05]"
              style={{ color: "#E0F2FE", textWrap: "balance" }}>
            Book. Course. Audio.<br/>
            <span style={{ color: GOLD }}>One Price.</span>
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg" style={{ color: "rgba(255,253,245,0.92)" }}>
            Three ways to absorb the same life-changing message — so it sticks no matter how you learn best.
          </p>

          <div className="grid md:grid-cols-3 gap-5 mt-9">
            {[
              { Icon: BookOpen, title: "Inspiring Book", body: "Powerful insights to strengthen your mindset and transform your life. 10 plain-English chapters." },
              { Icon: GraduationCap, title: "Video Course", body: "Step-by-step guidance with practical lessons to help you grow and succeed. Watch at your own pace." },
              { Icon: Headphones, title: "Audio Program", body: "Listen anywhere, anytime. Stay motivated on your drive, your walk, or while you work." },
            ].map((b) => (
              <div key={b.title} className="rounded-2xl p-6 text-left"
                   style={{ background: "rgba(255,253,245,0.08)", border: `2px solid ${GOLD}66` }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 shadow"
                     style={{ background: GOLD, color: NAVY }}>
                  <b.Icon className="w-6 h-6"/>
                </div>
                <h3 className="font-serif-display text-2xl uppercase leading-tight" style={{ color: "#E0F2FE" }}>
                  {b.title}
                </h3>
                <p className="mt-2 text-sm md:text-base leading-relaxed" style={{ color: "rgba(255,253,245,0.85)" }}>
                  {b.body}
                </p>
              </div>
            ))}
          </div>

          <Link to={BUY_HREF}
                data-testid="with-faith-bundle-cta"
                className="mt-9 inline-flex items-center gap-3 px-9 py-5 rounded-md text-lg md:text-xl font-bold uppercase tracking-[0.18em] shadow-2xl hover:scale-[1.03] transition"
                style={{ background: GOLD, color: NAVY, border: `3px solid ${GOLD}` }}>
            <Heart className="w-5 h-5"/> Get The Bundle Now <ArrowRight className="w-5 h-5"/>
          </Link>
          <p className="mt-3 text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: "rgba(255,229,166,0.75)" }}>
            ★ 30-Day Money-Back Guarantee ★ Instant Course & Audio Access
          </p>
        </div>
      </section>

      {/* ════════════ FINAL CTA ════════════ */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-14 text-center">
        <h2 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight" style={{ color: NAVY }}>
          Your Next Chapter <span style={{ color: GOLD_DK }}>Starts Today.</span>
        </h2>
        <p className="mt-3 text-lg" style={{ color: INK }}>
          Don't put it on a shelf for "someday." Mike wrote this for the person reading it right now — that's you.
        </p>
        <Link to={BUY_HREF}
              data-testid="with-faith-final-cta"
              className="mt-7 inline-flex items-center gap-3 px-10 py-5 rounded-md text-lg md:text-xl font-bold uppercase tracking-[0.18em] shadow-xl hover:scale-[1.03] transition"
              style={{ background: GOLD, color: NAVY, border: `3px solid ${NAVY}` }}>
          <Heart className="w-6 h-6"/> Get The Bundle <ArrowRight className="w-6 h-6"/>
        </Link>
      </section>

      {!embedded && <SiteFooter />}

      <StickyBuyBar />
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   CHAPTER WALL — 10 cards, each: image + chapter number + real title
   + real opening excerpt + "the rest is inside" CTA.
   ════════════════════════════════════════════════════════════════════ */
function ChapterWall() {
  return (
    <section id="chapters" data-testid="chapter-wall"
             className="max-w-6xl mx-auto px-5 md:px-8 py-14 md:py-20">
      <div className="text-center max-w-3xl mx-auto mb-10">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: GOLD_DK }}>
          ★ 10 CHAPTERS · A TASTE OF WHAT'S INSIDE
        </div>
        <h2 className="font-serif-display text-4xl md:text-5xl uppercase leading-[1.05]"
            style={{ color: NAVY, textWrap: "balance" }}>
          A Few Words From Every Chapter.
        </h2>
        <p className="text-base md:text-lg mt-4 italic max-w-2xl mx-auto" style={{ color: INK }}>
          Real prose from Mike's book — not summaries, not AI rewrites. Read the opening of all 10 chapters below.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8">
        {CHAPTERS.map((ch) => (
          <article key={ch.n}
                   data-testid={`chapter-card-${ch.n}`}
                   className="rounded-2xl overflow-hidden bg-white shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition flex flex-col"
                   style={{ border: `2px solid ${GOLD_DK}` }}>
            {/* IMAGE */}
            <div className="relative aspect-[16/10] overflow-hidden" style={{ background: "#FAFCFF" }}>
              <img src={`/with-faith/chapters/chapter-${String(ch.n).padStart(2, "0")}.png`}
                   alt={`Chapter ${ch.n} — ${ch.title}`}
                   loading="lazy"
                   className="w-full h-full object-contain"/>
              <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] tracking-[0.28em] uppercase font-bold shadow"
                   style={{ background: "#FFFFFF", color: GOLD }}>
                ★ CHAPTER {ch.n}
              </div>
            </div>

            {/* TEXT */}
            <div className="p-5 md:p-7 flex-1 flex flex-col">
              <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-[1.1]"
                  style={{ color: NAVY, fontWeight: 700, textWrap: "balance" }}>
                {ch.title}
              </h3>
              <p className="mt-4 text-base md:text-lg leading-relaxed" style={{ color: INK }}>
                {ch.excerpt}
              </p>
              <div className="mt-5 pt-4 flex items-center justify-between flex-wrap gap-2"
                   style={{ borderTop: `1.5px dashed ${GOLD_DK}77` }}>
                <span className="text-[11px] tracking-[0.22em] uppercase font-bold italic" style={{ color: GOLD_DK }}>
                  ★ The rest is inside the book.
                </span>
                <Link to={BUY_HREF}
                      data-testid={`chapter-card-cta-${ch.n}`}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[11px] font-bold uppercase tracking-[0.18em] shadow hover:scale-[1.03] transition"
                      style={{ background: "#FFFFFF", color: GOLD }}>
                  Read The Rest <ArrowRight className="w-3.5 h-3.5"/>
                </Link>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Mid-page CTA so visitors don't have to scroll all the way back up */}
      <div className="text-center mt-12">
        <Link to={BUY_HREF}
              data-testid="with-faith-mid-cta"
              className="inline-flex items-center gap-3 px-9 py-4 rounded-md text-base md:text-lg font-bold uppercase tracking-[0.18em] shadow-xl hover:scale-[1.03] transition"
              style={{ background: GOLD, color: NAVY, border: `3px solid ${NAVY}` }}>
          <Heart className="w-5 h-5"/> Get The Bundle <ArrowRight className="w-5 h-5"/>
        </Link>
        <p className="mt-3 text-[11px] uppercase tracking-[0.22em] font-bold italic" style={{ color: GOLD_DK }}>
          Book + Video Course + Audio Program — One Order
        </p>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
   STICKY BUY BAR — slim pill that fades in after the hero. Dismissible
   for the session so it never nags. ════════════════════════════════════ */
function StickyBuyBar() {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(
    typeof window !== "undefined" && sessionStorage.getItem("withfaith_bar_dismissed") === "1"
  );

  useEffect(() => {
    if (dismissed) return;
    const onScroll = () => setShow(window.scrollY > 700);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dismissed]);

  if (dismissed || !show) return null;

  return (
    <div data-testid="withfaith-sticky-buy-bar"
         className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[94%] sm:w-auto"
         style={{ maxWidth: 600 }}>
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full shadow-lg backdrop-blur-md"
           style={{ background: "rgba(22,36,74,0.94)", border: `1.5px solid ${GOLD}` }}>
        <Heart className="w-4 h-4 flex-shrink-0" style={{ color: GOLD }}/>
        <span className="text-xs sm:text-sm font-semibold whitespace-nowrap" style={{ color: "#FFFFFF" }}>
          Like what you're readin'?
        </span>
        <Link to={BUY_HREF}
              data-testid="withfaith-sticky-cta-btn"
              className="ml-1 px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold uppercase tracking-wider hover:scale-[1.04] transition whitespace-nowrap"
              style={{ background: GOLD, color: NAVY }}>
          Get The Bundle
        </Link>
        <button type="button"
                data-testid="withfaith-sticky-dismiss-btn"
                aria-label="Dismiss"
                onClick={() => { sessionStorage.setItem("withfaith_bar_dismissed", "1"); setDismissed(true); }}
                className="ml-1 w-6 h-6 flex items-center justify-center rounded-full opacity-70 hover:opacity-100 transition"
                style={{ color: "#FFFFFF" }}>
          <span className="text-base leading-none">×</span>
        </button>
      </div>
    </div>
  );
}
